import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from '@shared/router/index'
import { Icon } from '@iconify/vue'
import permissionButtonDirective from '@shared/directives/permissionButton'

// Package 폴더의 Vuetify 설정 사용
import vuetify from '@shared/plugins/vuetify'

//i18n import
import { createI18n } from 'vue-i18n';
import messages, { loadLanguageMetadata, getSupportedLanguages } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { syncTutorialLanguage } from '@/shared/utils/tutorialLang';

// Package 폴더의 스타일 사용
import '@shared/scss/style.scss'

// PreSet plug-in 등록 (jurisdiction 별 chatflows / panels / e-Filing / 주소데이터를
//   shared registry 에 등록). SG 추가 시 같은 줄 패턴으로 `.../sg/preset` import 추가.
import '@/features/startTask/preset_task/my/preset';
import '@/features/startTask/preset_task/sg/preset';
// My Preset (클라이언트 커스텀) — Aura Incorporation 전용 패널 자동 등록
import '@/features/startTask/my_preset/sg/aura';
// My Preset (클라이언트 커스텀) — Singbac Incorporation Post 전용 패널 자동 등록
import '@/features/startTask/my_preset/sg/singbac';
// My Preset (클라이언트 커스텀) — Singapura Verification of Data(CSI001) 전용 패널 자동 등록
import '@/features/startTask/my_preset/sg/singapura';
// My Preset (클라이언트 커스텀) — Aventross AGM (Audited Companies, CAV001) 전용 패널 자동 등록
import '@/features/startTask/my_preset/sg/aventross';

// 추가 플러그인들
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
// @ts-ignore - vue3-apexcharts 타입 선언 문제 해결
import VueApexCharts from 'vue3-apexcharts';
import VueTablerIcons from 'vue-tabler-icons';
import Maska from 'maska';
import VueEasyLightbox from 'vue-easy-lightbox';
import VueScrollTo from 'vue-scrollto';

// localStorage에서 저장된 언어 가져오기 (없으면 브라우저 언어 감지)
const getInitialLocale = (): string => {
    const savedLanguage = localStorage.getItem('preferred-language')
    if (savedLanguage) {
        return savedLanguage
    }

    // 브라우저 언어 감지
    const browserLanguage = navigator.language.toLowerCase()
    // messages.ts에서 지원 언어 목록 가져오기
    const supportedLanguagesList = getSupportedLanguages()
    const supportedLanguageCodes = supportedLanguagesList.map(lang => lang.code)

    // 전체 언어 코드로 먼저 확인 (예: zh-tw, zh-cn)
    if (supportedLanguageCodes.includes(browserLanguage)) {
        return browserLanguage
    }

    // 첫 번째 부분만 확인 (예: ko-KR -> ko)
    const shortCode = browserLanguage.split('-')[0]

    // 영어권인 경우 en-GB (영국 국기)를 기본으로 사용
    if (shortCode === 'en') {
        return 'en-GB'
    }

    if (supportedLanguageCodes.includes(shortCode)) {
        return shortCode
    }

    // 기본값: en-GB (영어/영국 국기)
    return 'en-GB'
}

const i18n = createI18n({
    legacy: false, // Composition API 사용
    locale: getInitialLocale(),
    // ✅ 누락 키는 무조건 영어로 폴백(한글 등 다른 언어로 새지 않도록). en-GB → en 순.
    fallbackLocale: ['en', 'en-GB'],
    messages: messages,
    silentTranslationWarn: true,
    silentFallbackWarn: true
});

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(PerfectScrollbarPlugin)
app.use(VueTablerIcons)
app.use(i18n)
app.use(Maska)
app.use(VueApexCharts)
app.use(vuetify)
app.use(VueEasyLightbox)
app.use(VueScrollTo, {
    duration: 1000,
    easing: 'ease'
})

// Icon 컴포넌트 전역 등록
app.component('Icon', Icon)

// 전역 디렉티브 등록
app.directive('permission-button', permissionButtonDirective)

// 언어 스토어 초기화 (앱 마운트 전에 i18n 인스턴스 연결)
const languageStore = useLanguageStore()
languageStore.setI18nInstance(i18n.global)

// i18n locale과 languageStore 동기화
const initialLocale = i18n.global.locale.value
if (languageStore.getCurrentLanguage() !== initialLocale) {
    languageStore.setLanguage(initialLocale)
}
// 튜토리얼/가이드 HTML 언어(lt_lang)를 시작 시점에 무조건 현재 앱 언어와 맞춤
// (영문=기본값일 때 setLanguage 가 호출되지 않아 누락되던 케이스 방지)
syncTutorialLanguage(languageStore.getCurrentLanguage())
console.log('🌐 i18n initialized with locale:', initialLocale)
console.log('🌐 Language store initialized:', languageStore.getCurrentLanguage())

// L_LANGUAGES 테이블에서 언어 메타데이터 로드 및 초기 번역 파일 로드
const initializeTranslations = async () => {
    try {
        // 1. 언어 메타데이터 로드
        await loadLanguageMetadata();
        console.log('✅ Language metadata loaded from L_LANGUAGES table');

        // 2. 초기 번역 파일 로드
        // messages.ts의 정적 import.meta.glob 사용 (번들에 포함됨, 개발/운영 공통)
        const { updateGlobalTranslationCache, getStaticModuleTranslations } = await import('@/utils/locales/messages');
        const initialLocale = getInitialLocale();

        console.log('🌐 Pre-loading translations for:', initialLocale);
        console.log('🌐 Loading all translations from static bundle...');

        // 정적으로 번들된 번역 데이터 가져오기
        const staticTranslations = getStaticModuleTranslations();

        // 경로에서 언어 코드와 모듈 이름 추출하는 helper 함수
        const getLocaleFromPath = (path: string): string | null => {
            const match = path.match(/([a-z]{2,3}(?:-[a-z]{2})?)\.json$/i);
            return match ? match[1].toLowerCase() : null;
        };

        const getModuleNameFromPath = (path: string): string | null => {
            const match = path.match(/modules\/([^/]+)\//);
            return match ? match[1] : null;
        };

        // 모듈별로 번역 데이터 캐시에 저장 및 vue-i18n messages 업데이트
        const localeMessages: Record<string, Record<string, any>> = {};
        
        Object.entries(staticTranslations).forEach(([path, module]) => {
            const locale = getLocaleFromPath(path);
            const moduleName = getModuleNameFromPath(path);

            if (locale && moduleName) {
                const translationData = (module as any).default || module;
                updateGlobalTranslationCache(locale, moduleName, translationData);

                // vue-i18n messages 객체 업데이트를 위한 데이터 수집
                if (!localeMessages[locale]) {
                    localeMessages[locale] = {};
                }
                localeMessages[locale] = { ...localeMessages[locale], ...translationData };

                if (locale === initialLocale) {
                    console.log(`✅ Loaded from bundle: ${moduleName}/${locale}`);
                }
            }
        });

        // vue-i18n messages 객체 업데이트 (모든 언어에 대해)
        Object.entries(localeMessages).forEach(([locale, messages]) => {
            i18n.global.setLocaleMessage(locale, messages);
            console.log(`✅ Updated vue-i18n messages for locale: ${locale}`);
        });

        console.log(`✅ All translations loaded for ${initialLocale} from static bundle`);

        console.log('✅ Initial translations fully loaded, app ready');
    } catch (error) {
        console.warn('⚠️ Failed to initialize translations:', error);
    }
};

// 언어 변경 시 번역 파일 동적 로드
const setupLanguageChangeListener = async () => {
    window.addEventListener('language-changed', async (event: any) => {
        const newLanguage = event.detail?.language;
        if (!newLanguage) return;

        try {
            // ✅ 로컬 개발(development/localdev)에서는 이미 번들에 모든 번역이 포함되어 있음.
            // 여기서 public/locales 를 다시 로드 + setLocaleMessage로 덮어쓰면
            // 일부 모듈만 로드된 상태로 기존 메시지가 교체되어 "다국어가 풀리는" 현상이 발생할 수 있음.
            const isLocalDev = import.meta.env.DEV && (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'localdev');
            if (isLocalDev) {
                if (i18n && i18n.global) {
                    i18n.global.locale.value = newLanguage;
                    console.log(`🔄 i18n locale updated (local dev): ${newLanguage}`);
                }
                return;
            }

            const { updateGlobalTranslationCache, detectModules } = await import('@/utils/locales/messages');
            const modules = detectModules(); // 모듈 자동 감지 (서버/운영 환경)

            console.log(`🌐 Loading translations for language change: ${newLanguage}`);
            console.log('📁 Modules:', modules);

            // 서버/운영: public/locales/modules 에서 동적 로드
            const { LocaleLoader } = await import('@/utils/locales/dynamicLoader');
            const loader = new LocaleLoader();

            const loadedByModule = await loader.loadMultipleModules(modules, newLanguage);

            // 전역 캐시 업데이트 + vue-i18n 업데이트용 데이터 수집
            const mergedMessages = Object.values(loadedByModule).reduce((acc, moduleMessages) => {
                return { ...acc, ...moduleMessages };
            }, {} as Record<string, any>);

            Object.entries(loadedByModule).forEach(([moduleName, translations]) => {
                if (translations) {
                    updateGlobalTranslationCache(newLanguage, moduleName, translations);
                }
            });

            // ✅ 기존 locale message에 "병합"하여 누락 모듈로 인해 기존 번역이 사라지지 않게 함
            const existingMessages = i18n.global.getLocaleMessage(newLanguage) as any;
            const nextMessages = { ...(existingMessages || {}), ...mergedMessages };

            if (Object.keys(nextMessages).length > 0) {
                i18n.global.setLocaleMessage(newLanguage, nextMessages);
                console.log(`✅ Updated vue-i18n messages for locale: ${newLanguage}`);
            }

            // i18n locale 업데이트 (번역 데이터 로드 후)
            if (i18n && i18n.global) {
                i18n.global.locale.value = newLanguage;
                console.log(`🔄 i18n locale force updated to: ${newLanguage}`);
            }
        } catch (error) {
            console.warn(`⚠️ Failed to load translations for ${newLanguage}:`, error);
        }
    });
};

// 정적 번역은 이미 i18n에 포함됨 — 앱을 먼저 마운트하고 메타데이터 API는 백그라운드 로드
setupLanguageChangeListener();
app.mount('#app');
void initializeTranslations();
